// The organizer's records live only in this browser (IndexedDB). Nothing here talks to a server.
import { useCallback, useEffect, useRef, useState } from "react";
import { addPerson, emptyYearFile, type Snapshot, type YearFile } from "@ftr/tax-core";

const DB_NAME = "ftr-organizer";
const DB_VERSION = 1;
const YEARS = "years";
const SNAPSHOTS = "snapshots";

export interface SavedSnapshot {
  id: string;
  taxYear: number;
  label: string;
  snapshot: Snapshot;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("This browser can't save data on this device."));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(YEARS)) db.createObjectStore(YEARS);
      if (!db.objectStoreNames.contains(SNAPSHOTS)) db.createObjectStore(SNAPSHOTS, { keyPath: "id" }).createIndex("taxYear", "taxYear");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Couldn't open on-device storage."));
  });
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error ?? new Error("Couldn't save on this device."));
        tx.onabort = () => reject(tx.error ?? new Error("Couldn't save on this device."));
      }),
  );
}

export function newId(): string {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function starterYearFile(taxYear: number): YearFile {
  return addPerson(emptyYearFile(taxYear), { id: newId(), label: "You", role: "primary" });
}

export type SaveState = "loading" | "saved" | "saving" | "unavailable";

/** The year file plus an updater. Changes save to this device right away. */
export function useYearFile(taxYear: number) {
  const [yf, setYf] = useState<YearFile | null>(null);
  const [state, setState] = useState<SaveState>("loading");
  const latest = useRef<YearFile | null>(null);

  useEffect(() => {
    let cancelled = false;
    run<YearFile | undefined>(YEARS, "readonly", (s) => s.get(taxYear))
      .then((found) => {
        if (cancelled) return;
        const v = found ?? starterYearFile(taxYear);
        latest.current = v;
        setYf(v);
        setState("saved");
      })
      .catch(() => {
        if (cancelled) return;
        const v = starterYearFile(taxYear);
        latest.current = v;
        setYf(v);
        setState("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [taxYear]);

  const update = useCallback(
    (fn: (yf: YearFile) => YearFile) => {
      const current = latest.current;
      if (!current) return;
      const next = fn(current);
      if (next === current) return;
      latest.current = next;
      setYf(next);
      setState((s) => (s === "unavailable" ? s : "saving"));
      run(YEARS, "readwrite", (s) => s.put(next, taxYear))
        .then(() => setState((s) => (s === "unavailable" || latest.current !== next ? s : "saved")))
        .catch(() => setState("unavailable"));
    },
    [taxYear],
  );

  const replace = useCallback((next: YearFile) => update(() => next), [update]);
  return { yf, update, replace, state };
}

/** Write a year file directly, for restoring a backup of a year that isn't open. */
export function putYearFile(yf: YearFile): Promise<unknown> {
  return run(YEARS, "readwrite", (s) => s.put(yf, yf.taxYear));
}

export async function listSnapshots(taxYear: number): Promise<SavedSnapshot[]> {
  const all = await run<SavedSnapshot[]>(SNAPSHOTS, "readonly", (s) => s.index("taxYear").getAll(taxYear));
  return all.sort((a, b) => (a.snapshot.createdAt < b.snapshot.createdAt ? 1 : -1));
}

export function saveSnapshot(s: SavedSnapshot): Promise<unknown> {
  return run(SNAPSHOTS, "readwrite", (store) => store.put(s));
}

export function deleteSnapshot(id: string): Promise<unknown> {
  return run(SNAPSHOTS, "readwrite", (store) => store.delete(id));
}

/** Remove everything the organizer stored on this device. */
export function clearAll(): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}
